import { useAppForm as useAppFormBase } from '@/components/custom/form'
import { useZodI18n } from './zod-i18n'

/**
 * Wrap the base form hook so that zod's i18n setup (and the per-locale zod messages chunk) only
 * loads on routes that actually render a form.
 *
 * ⚠️ ASYNC-LOADED EDIT FORMS: `defaultValues` are read ONCE, at mount. If you derive them from a
 * `useQuery` result, do NOT mount this hook while that query is still loading and rely on
 * defaultValues "reactivity" — TanStack only re-syncs defaultValues into fields that haven't been
 * touched, so under load a field touched before the data arrives keeps its empty default and
 * silently fails validation (the submit just no-ops). `form.reset(value)` does not reliably fix it
 * either (TanStack #1490).
 *
 * Pattern: render the form inside a `key`ed wrapper whose key encodes BOTH the record id and whether
 * its data has loaded (e.g. `key={`${id ?? 'new'}:${dataReady ? 'ready' : 'loading'}`}`). When the
 * data arrives the key flips and React remounts the form, so it re-reads complete defaultValues at
 * mount — no reset()/sync-effect hacks. The remount happens within the single round-trip right after
 * the dialog opens, so there is no user input to clobber. See `companies/employee-form.tsx`
 * (EmployeeForm → EmployeeFormBody) for the canonical implementation. org-form / team-form / job-form
 * share the same `query → useAppForm` shape and the same latent risk if their required fields grow.
 */
export const useAppForm: typeof useAppFormBase = (...args) => {
  useZodI18n()
  return useAppFormBase(...args)
}
